import { TablePageLayout } from '@/layouts/TablePageLayout';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import App from 'antd/es/app';
import Typography from 'antd/es/typography';
import { AxiosError } from 'axios';
import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import {
  DataTable,
  createActionColumn,
  useDataTableState,
} from 'ui-components';
import type { ActionItem, DataTableColumnType } from 'ui-components';

import type { AgentWithMetrics } from '@mai/platform-api/src/routes/agents.schema';

import { CreateAgentModal } from '../../../components/AgentModals';
import { useAgents, useDeleteAgent } from '../../../queries/agents';
import { formatRelativeTime } from '../../../utils/format';

const { Text, Title } = Typography;

type AgentRow = AgentWithMetrics;

const AgentName = styled(Link)`
  ${({ theme }) => `
  font-weight: ${theme.fontWeightStrong / 1.2};
  &:hover {
    text-decoration: underline;
  }
`}
`;

const EmptyStateIcon = styled(RobotOutlined)`
  ${({ theme }) => `
  font-size: ${theme.sizeXXL}px;
`}
`;

const SuccessRate = styled.span<{ $rate: number }>`
  ${({ theme, $rate }) => `
  color: ${$rate >= 90 ? theme.colorSuccessText : $rate >= 70 ? theme.colorWarningText : theme.colorErrorText};
`}
`;

const EmptyStateContainer = styled.div`
  ${({ theme }) => `
  text-align: center;
  padding: ${theme.paddingXL}px;
  background: ${theme.colorBgContainer};
  border-radius: ${theme.borderRadius}px;
  margin: ${theme.marginLG}px 0;
`}
`;



const GuidanceStep = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: flex-start;
  gap: ${theme.marginMD}px;
  margin-bottom: ${theme.marginLG}px;
  text-align: left;
  
  .step-number {
    background: ${theme.colorPrimary};
    color: white;
    width: ${theme.controlHeight}px;
    height: ${theme.controlHeight}px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: ${theme.fontWeightStrong};
    font-size: ${theme.fontSizeLG}px;
    flex-shrink: 0;
  }
  
  .step-content {
    flex: 1;
  }
`}
`;

export const AgentsPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { message, modal } = App.useApp();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { requestState } = useDataTableState({
    filterMap: {
      name: 'nameLike',
    },
  });

  const {
    data: agentsData,
    isLoading,
    error,
  } = useAgents(
    {
      ...requestState,
      teamIds: teamId ? [teamId] : undefined,
    },
    {
      enabled: !!teamId,
    },
  );

  const deleteAgentMutation = useDeleteAgent();



  const handleDeleteAgent = (agent: AgentRow) => {
    void modal.confirm({
      title: 'Delete Agent',
      content: `Are you sure you want to delete "${agent.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteAgentMutation.mutateAsync(agent.agentId);
          void message.success('Agent deleted successfully');
        } catch (error) {
          const errorMessage =
            error instanceof AxiosError
              ? error.response?.data?.error?.message || 'Failed to delete agent'
              : 'Failed to delete agent';
          void message.error(errorMessage);
        }
      },
    });
  };

  const getAgentActions = (agent: AgentRow): ActionItem[] => [
    {
      key: 'configure',
      label: 'Configure',
      icon: <SettingOutlined />,
      onClick: () => {
        // TODO: Open configure modal
      },
    },
    {
      key: 'run',
      label: 'Run',
      icon: <PlayCircleOutlined />,
      onClick: () => {
        // TODO: Open run modal
      },
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDeleteAgent(agent),
    },
  ];

  const columns: DataTableColumnType<AgentRow>[] = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        render: (name: string, record: AgentRow) => (
          <AgentName to={`/team/${teamId}/agents/${record.agentId}`}>
            {name}
          </AgentName>
        ),
        sorter: true,
        filter: {
          type: 'search',
          placeholder: 'Search by name...',
        },
      },
      {
        title: 'Last Run',
        dataIndex: 'lastRunAt',
        key: 'lastRunAt',
        width: 150,
        render: (date: Date | null) =>
          date ? formatRelativeTime(date) : 'Never',
      },
      {
        title: 'Total Runs',
        dataIndex: 'totalRuns',
        key: 'totalRuns',
        width: 100,
        align: 'right',
        render: (count: number) => count.toLocaleString(),
      },
      {
        title: 'Success Rate',
        dataIndex: 'successRate',
        key: 'successRate',
        width: 120,
        align: 'right',
        render: (rate: number) => {
          return <SuccessRate $rate={rate}>{rate.toFixed(1)}%</SuccessRate>;
        },
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 150,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      {
        title: 'Last Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 150,
        render: (date: Date) => formatRelativeTime(date),
        sorter: true,
      },
      createActionColumn({
        getActions: getAgentActions,
        width: 80,
      }),
    ],
    [teamId],
  );

  if (error) {
    return (
      <div>
        Error loading agents:{' '}
        {error instanceof AxiosError
          ? error.response?.data?.error?.message || error.message
          : 'Unknown error'}
      </div>
    );
  }

  return (
    <TablePageLayout
      title="Agents"
      description="Manage and configure your AI agents."
      actions={[
        {
          label: 'Create Agent',
          type: 'primary',
          icon: <PlusOutlined />,
          onClick: () => setIsCreateModalOpen(true),
        },
      ]}
      loading={isLoading}
    >
      <DataTable
        columns={columns}
        data={agentsData?.items || []}
        rowKey="agentId"
        loading={isLoading}
        maxVisibleFilters={3}
        pagination={{
          current: agentsData?.pagination?.page || 1,
          pageSize: agentsData?.pagination?.pageSize || 20,
          total: agentsData?.pagination?.totalItems || 0,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} agents`,
        }}
        emptyState={{
          icon: <EmptyStateIcon />,
          title: 'Welcome to Agents!',
          description: (
            <EmptyStateContainer>
              <Text
                type="secondary"
                style={{
                  fontSize: '16px',
                  marginBottom: '24px',
                  display: 'block',
                }}
              >
                AI agents automate complex tasks and workflows using natural language instructions.
              </Text>

              <div style={{ marginBottom: '32px' }}>
                <Title level={5} style={{ marginBottom: '16px' }}>
                  Get Started in 3 Steps
                </Title>

                <GuidanceStep>
                  <div className="step-number">1</div>
                  <div className="step-content">
                    <Text strong>Define Your Agent</Text>
                    <br />
                    <Text type="secondary">
                      Create an agent with a clear purpose and the tools it needs to accomplish tasks.
                    </Text>
                  </div>
                </GuidanceStep>

                <GuidanceStep>
                  <div className="step-number">2</div>
                  <div className="step-content">
                    <Text strong>Configure & Train</Text>
                    <br />
                    <Text type="secondary">
                      Set up your agent's behavior, add context, and configure its capabilities.
                    </Text>
                  </div>
                </GuidanceStep>

                <GuidanceStep>
                  <div className="step-number">3</div>
                  <div className="step-content">
                    <Text strong>Run & Monitor</Text>
                    <br />
                    <Text type="secondary">
                      Execute your agent, monitor its performance, and iterate on improvements.
                    </Text>
                  </div>
                </GuidanceStep>
              </div>
            </EmptyStateContainer>
          ),
          action: {
            label: 'Create Your First Agent',
            onClick: () => setIsCreateModalOpen(true),
            icon: <PlusOutlined />,
          },
          overrideOnFiltersPresent: {
            icon: <EmptyStateIcon />,
            title: 'No agents match your filters',
            description: 'Try adjusting your filters to see more results.',
            action: undefined,
          },
        }}
      />

      <CreateAgentModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        teamId={teamId!}
      />
    </TablePageLayout>
  );
};
